import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Avatar } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';

export default function ChatMessages({
  messages = [],
  isThinking = false,
  isDark,
}) {
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  const renderHtml = text =>
    DOMPurify.sanitize(
      marked.parse(text || '', { breaks: true }),
      { USE_PROFILES: { html: true } }
    );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 2,
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        background: '#f7faea',
      }}
    >
      {messages.map((m, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            mb: 2,
            animation: 'fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes fadeInUp': {
              from: {
                opacity: 0,
                transform: 'translateY(20px) scale(0.95)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0) scale(1)',
              },
            },
          }}
        >
          {/* Avatar */}
          <Avatar
            sx={{
              bgcolor:
                m.role === 'assistant'
                  ? isDark ? '#B1D23A' : '#819C24'
                  : isDark ? '#414437' : '#BDC0B2',
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            {m.role === 'assistant' ? (
              <SmartToyIcon sx={{ fontSize: 22 }} />
            ) : (
              <PersonIcon sx={{ fontSize: 22 }} />
            )}
          </Avatar>

          {/* Name + Message */}
          <Box sx={{ maxWidth: '70%' }}>
            {/* Name */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {m.role === 'assistant' ? 'SmartGrow AI' : 'You'}
            </Typography>

            {/* Message bubble */}
            <Typography
              component="div"
              sx={{
                bgcolor: m.role === 'user' ? '#ffffff' : '#e6f1c1',
                p: 1.5,
                borderRadius: 2,
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{
                __html: renderHtml(m.content),
              }}
            />
          </Box>
        </Box>
      ))}


      {/* 🤖 THINKING BUBBLE */}
      {isThinking && (
        <Box
          sx={{
            bgcolor: '#fff',
            p: 1.5,
            borderRadius: 2,
            mb: 1,
            maxWidth: '70%',
            opacity: 0.8,
          }}
        >
          <Typography variant="body2">🤖 Thinking…</Typography>
        </Box>
      )}

      <div ref={endRef} />
    </Box>
  );
}
